// FAQ section with accordion + Schema.org FAQPage injection (huge SEO win)
import { useState, useEffect } from "react";
import { ChevronDown, HelpCircle } from "lucide-react";
import { useLang } from "../contexts/LangContext";
import { FAQS, FAQ_CATEGORIES } from "../data/faqs";

export default function FAQSection() {
  const { lang, t } = useLang();
  const ct = t.faq;
  const cats = FAQ_CATEGORIES[lang] || FAQ_CATEGORIES.de;
  const items = FAQS[lang] || FAQS.de;

  const [activeCat, setActiveCat] = useState(cats[0].key);
  const [openId, setOpenId] = useState(items[0]?.id);

  // Inject JSON-LD FAQPage schema for Google rich snippets
  useEffect(() => {
    const allItems = FAQS[lang] || FAQS.de;
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": allItems.map((it) => ({
        "@type": "Question",
        "name": it.q,
        "acceptedAnswer": { "@type": "Answer", "text": it.a }
      }))
    };
    const id = "faq-schema-jsonld";
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.id = id;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(schema);
    return () => { /* keep schema mounted */ };
  }, [lang]);

  const filtered = items.filter((it) => it.cat === activeCat);

  return (
    <section className="py-16 lg:py-24 bg-white dark:bg-[#0F1012]" data-testid="faq-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-12">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-3">
            {ct.eyebrow}
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mb-4">
            {ct.title}
          </h2>
          <p className="text-sm sm:text-base text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto leading-relaxed">
            {ct.subtitle}
          </p>
        </div>

        {/* Category tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {cats.map((c) => {
            const isActive = c.key === activeCat;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => {
                  setActiveCat(c.key);
                  const first = items.find((it) => it.cat === c.key);
                  setOpenId(first?.id);
                }}
                className={`px-4 py-2 text-xs font-bold tracking-wider uppercase border transition-colors ${
                  isActive
                    ? "bg-[#090A0B] text-white border-[#090A0B]"
                    : "bg-white dark:bg-[#0F1012] text-neutral-700 dark:text-neutral-300 border-neutral-300 dark:border-neutral-700 hover:border-[#FF5A00]"
                }`}
                data-testid={`faq-cat-${c.key}`}
              >
                {c.label}
              </button>
            );
          })}
        </div>

        {/* Accordion */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
          <div className="space-y-2">
            {filtered.map((it) => {
              const isOpen = openId === it.id;
              return (
                <div
                  key={it.id}
                  className="border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#0A0B0D]"
                >
                  <button
                    type="button"
                    onClick={() => setOpenId(isOpen ? null : it.id)}
                    className="w-full text-start px-5 py-4 flex items-center gap-4 hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-colors"
                    aria-expanded={isOpen}
                    data-testid={`faq-question-${it.id}`}
                  >
                    <span className="font-bold text-base sm:text-lg flex-1 leading-snug">
                      {it.q}
                    </span>
                    <ChevronDown
                      size={18}
                      className={`shrink-0 text-[#FF5A00] transition-transform ${isOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {isOpen && (
                    <div
                      className="px-5 pb-5 pt-1 text-sm sm:text-[15px] text-neutral-600 dark:text-neutral-400 leading-relaxed border-t border-neutral-100 dark:border-neutral-800 animate-[slideUp_.25s_ease-out]"
                      data-testid={`faq-answer-${it.id}`}
                    >
                      {it.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Side: contact card */}
          <aside className="bg-gradient-to-br from-[#090A0B] to-[#1A1B1F] text-white p-6 sm:p-7 h-fit sticky top-24">
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#FF5A00]/20 text-[#FF5A00] mb-4">
              <HelpCircle size={20} />
            </div>
            <h3 className="text-xl font-black tracking-tighter mb-2">{ct.help_title}</h3>
            <p className="text-sm text-neutral-400 leading-relaxed mb-5">{ct.help_body}</p>
            <a
              href="tel:+491758802646"
              className="block bg-[#FF5A00] hover:bg-[#E04F00] text-center font-bold tracking-wider uppercase text-xs py-3 transition-colors mb-2"
              data-testid="faq-help-call"
            >
              +49 175 8802646
            </a>
            <a
              href="mailto:info@talkani.de"
              className="block border border-neutral-700 hover:bg-neutral-800 text-center font-bold tracking-wider uppercase text-xs py-3 transition-colors"
              data-testid="faq-help-email"
            >
              info@talkani.de
            </a>
          </aside>
        </div>
      </div>
    </section>
  );
}
